/**
 * Main entry point for AIT (Apps-in-Toss) bundle operations.
 */

import { AITReader, AITReaderOptions } from "./reader";
import { AITWriter, AITWriterOptions } from "./writer";
import { FORMAT_VERSION, MAGIC } from "./constants";
import { BundleFormat, Format } from "./format";
import { generateUUID7 } from "./utils";

/**
 * Main entry point for AIT bundle operations.
 *
 * @example
 * ```typescript
 * // Writing (auto-generated UUIDv7 for deploymentId)
 * const writer = AppsInTossBundle.writer({ appName: "app-name" });
 * writer.setMetadata({ platform: PlatformType.REACT_NATIVE });
 * writer.addFile("index.html", new TextEncoder().encode("<html>...</html>"));
 * const buffer = await writer.toBuffer();
 *
 * // Writing with explicit deploymentId
 * const writer = AppsInTossBundle.writer({
 *   deploymentId: "019bfa90-ad4c-799f-b227-b4159e6867f7",
 *   appName: "app-name",
 * });
 *
 * // Reading
 * const reader = AppsInTossBundle.reader(buffer);
 * console.log(reader.deploymentId);
 * console.log(reader.appName);
 * const content = await reader.readEntry("index.html");
 * ```
 */
export class AppsInTossBundle {
  private constructor() {
    throw new Error("AppsInTossBundle is a utility class and cannot be instantiated");
  }

  /**
   * Create a new writer.
   *
   * @param options Writer options including deploymentId and appName
   * @returns AITWriter instance
   */
  static writer(options: AITWriterOptions): AITWriter {
    return new AITWriter(options);
  }

  /**
   * Create a reader from a Uint8Array buffer.
   *
   * @param buffer AIT file contents
   * @param options Reader options
   * @returns AITReader instance
   */
  static reader(buffer: Uint8Array, options?: AITReaderOptions): AITReader {
    return AITReader.fromBuffer(buffer, options);
  }

  /**
   * Create a reader from an ArrayBuffer.
   *
   * @param buffer AIT file contents as ArrayBuffer
   * @param options Reader options
   * @returns AITReader instance
   */
  static readerFromArrayBuffer(buffer: ArrayBuffer, options?: AITReaderOptions): AITReader {
    return AITReader.fromArrayBuffer(buffer, options);
  }

  /**
   * Get the current format version.
   *
   * @returns Current format version
   */
  static getFormatVersion(): number {
    return FORMAT_VERSION;
  }

  /**
   * Get the magic bytes identifying AIT files.
   *
   * @returns Magic bytes
   */
  static getMagic(): Uint8Array {
    return MAGIC;
  }

  /**
   * Bundle format types.
   */
  static readonly Format = Format;

  /**
   * Detect the bundle format from a buffer.
   *
   * @param buffer - The buffer to detect format from (only first 8 bytes are needed)
   * @returns The detected format
   *
   * @example
   * ```typescript
   * const buffer = await fs.readFile("bundle.ait");
   * const format = AppsInTossBundle.detect(buffer);
   *
   * if (format === AppsInTossBundle.Format.AIT) {
   *   const reader = AppsInTossBundle.reader(buffer);
   *   // Process AIT bundle
   * } else if (format === AppsInTossBundle.Format.ZIP) {
   *   // Process legacy ZIP bundle
   * }
   * ```
   */
  static detect(buffer: Uint8Array): Format {
    return BundleFormat.detect(buffer);
  }

  /**
   * Check if the buffer represents an AIT format.
   *
   * @param buffer - The buffer to check (only first 8 bytes are needed)
   * @returns true if AIT format
   */
  static isAIT(buffer: Uint8Array): boolean {
    return BundleFormat.isAIT(buffer);
  }

  /**
   * Check if the buffer represents a ZIP format.
   *
   * @param buffer - The buffer to check (only first 4 bytes are needed)
   * @returns true if ZIP format
   */
  static isZIP(buffer: Uint8Array): boolean {
    return BundleFormat.isZIP(buffer);
  }

  /**
   * Generate a new UUIDv7 string.
   *
   * @returns A UUIDv7 string in standard format (8-4-4-4-12)
   */
  static generateUUID7(): string {
    return generateUUID7();
  }
}

